import {
  Popover,
  Progress,
  Box,
  rem,
  PasswordInputProps,
  Text,
} from "@mantine/core"
import { IconCheck, IconX } from "@tabler/icons-react"
import { t } from "i18next"
import { useTranslation } from "react-i18next"
import { DefaultPasswordInput } from "../../../-components/default-password-input"

interface Props {
  passwordInputProps: PasswordInputProps
  confirmPasswordInputProps: PasswordInputProps
}

export function ChangePasswordFormInputs({
  passwordInputProps,
  confirmPasswordInputProps,
}: Props) {
  const { getStrength } = useGetStrength()
  const { requirements } = useRequirements()
  const [popoverOpened, setPopoverOpened] = React.useState(false)

  const passwordValue = passwordInputProps.value?.toString() || ""

  const isValid = getStrength(passwordValue) === 100

  return (
    <>
      <Popover
        key={"password-popover"}
        opened={popoverOpened && !isValid}
        position="bottom"
        width="target"
        transitionProps={{ transition: "pop" }}
      >
        <Popover.Target>
          <div
            onFocusCapture={() => setPopoverOpened(true)}
            onBlurCapture={() => setPopoverOpened(false)}
          >
            <DefaultPasswordInput
              label={t("Password")}
              {...passwordInputProps}
            />
          </div>
        </Popover.Target>
        <Popover.Dropdown>
          <Progress
            color={
              getStrength(passwordValue) === 100
                ? "teal"
                : getStrength(passwordValue) > 50
                  ? "yellow"
                  : "red"
            }
            value={getStrength(passwordValue)}
            size={5}
            mb="xs"
          />

          {requirements.map((requirement, index) => (
            <PasswordRequirement
              key={index}
              label={requirement.label}
              meets={requirement.re.test(passwordValue)}
            />
          ))}
        </Popover.Dropdown>
      </Popover>
      <DefaultPasswordInput
        key={"confirm-password"}
        label={t("ConfirmPassword")}
        {...confirmPasswordInputProps}
      />
    </>
  )
}

function PasswordRequirement({
  meets,
  label,
}: {
  meets: boolean
  label: string
}) {
  return (
    <Text
      c={meets ? "teal" : "red"}
      style={{ display: "flex", alignItems: "center" }}
      mt={7}
      size="sm"
    >
      {meets ? (
        <IconCheck style={{ width: rem(14), height: rem(14) }} />
      ) : (
        <IconX style={{ width: rem(14), height: rem(14) }} />
      )}{" "}
      <Box ml={10}>{label}</Box>
    </Text>
  )
}

function useRequirements() {
  const { t } = useTranslation("app_server")
  return {
    requirements: [
      { re: /.{8,}/, label: t("PasswordRule_Length") },
      { re: /[0-9]/, label: t("PasswordRule_Number") },
      { re: /[a-z]/, label: t("PaswordRule_Lowercase") },
      { re: /[A-Z]/, label: t("PasswordRule_Uppercase") },
      {
        re: /[$&+,:;=?@#|'<>.^*()%!-]/,
        label: t("PasswordRule_SpecialCharacter"),
      },
    ],
  }
}
function useGetStrength() {
  const { requirements } = useRequirements()
  return {
    getStrength: (password: string) => {
      let multiplier = password.length > 5 ? 0 : 1

      requirements.forEach((requirement) => {
        if (!requirement.re.test(password)) {
          multiplier += 1
        }
      })

      return Math.max(100 - (100 / (requirements.length + 1)) * multiplier, 10)
    },
  }
}
